import { animate, state, style, transition, trigger } from "@angular/animations";
import { CdkMenu } from "@angular/cdk/menu";
import { OverlayModule } from "@angular/cdk/overlay";
import { Component } from "@angular/core";
import { RdxTooltipContentDirective, RdxTooltipTriggerDirective } from "@radix-ng/primitives/tooltip";
import { HeaderContext, injectFlexRenderContext } from "@tanstack/angular-table";
import { Download, FileSpreadsheet, FileText, LucideAngularModule, Trash2, X } from "lucide-angular";
import { OriButton } from "~/components/ui/button";
import { OriCheckbox } from "~/components/ui/checkbox";
import { OriTooltip, OriTooltipContent } from "~/components/ui/tooltip";
import * as XLSX from 'xlsx';




@Component({
    imports: [
        OriCheckbox,
        CdkMenu,
        OverlayModule,
        LucideAngularModule,
        OriButton,
        RdxTooltipContentDirective,
        OriTooltipContent,
        RdxTooltipTriggerDirective,
        OriTooltip,
    ],
    standalone: true,
    template: `
      <ori-checkbox
        [id]="checkboxId"
        (click)="onHeaderClick()" 
        type="button" 
        cdkOverlayOrigin 
        #trigger="cdkOverlayOrigin"
        [checked]="isChecked()"
        [indeterminate]="isIndeterminate()"
        aria-label="Select all"
      >
        </ori-checkbox>
      <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="trigger"
          [cdkConnectedOverlayOpen]="shouldShowOverlay()"
          (detach)="!shouldShowOverlay()"
      >
        <div 
          class="fixed flex-wrap inset-x-0 bottom-10 z-50 mx-auto flex items-center justify-center m-2 p-1 gap-0 w-fit rounded-md border bg-background text-foreground shadow-sm" 
          [@slideIn]="animationState"
          cdkMenu>
          
          <div class="flex rounded-md border pr-1 pl-2.5 h-8 items-center gap-1 bg-transparent">
            <span class="whitespace-nowrap text-xs">{{getSelectedCount()}} Selected</span>
            <div class="h-[20px] border bg-border shrink-0" style="margin: 0 1px"></div>
            
            <ng-container [openDelay]="0" oriTooltip>
              <button (click)="cancel()" oriButton class="border-none outline-none rounded-lg w-[25px] h-[25px]" variant="outline" rdxTooltipTrigger>
                <lucide-angular [img]="X" size="12" strokeWidth="2"/>
              </button>
              <ng-template [sideOffset]="16" rdxTooltipContent>
                <ori-tooltip-content class="dark px-2 py-1 text-xs">
                  Clear selection
                </ori-tooltip-content>
              </ng-template>
            </ng-container>
          </div>
          
          <div class="h-[20px] border bg-border shrink-0" style="margin: 0 4px"></div>

          <ng-container [openDelay]="0" oriTooltip>
            <button oriButton (click)="exportToExcel()" class="m-1 p-1 rounded-sm w-[28px] h-[28px]" rdxTooltipTrigger variant="secondary" size="sm">
              <lucide-angular [img]="Download" size="12" strokeWidth="2"/>
            </button>
            <ng-template [sideOffset]="12" rdxTooltipContent>
              <ori-tooltip-content class="dark px-2 py-1 text-xs">
                Export Excel
              </ori-tooltip-content>
            </ng-template>
          </ng-container>

    
          <ng-container [openDelay]="0" oriTooltip>
            <button oriButton class="m-1 p-1 rounded-sm w-[28px] h-[28px]" rdxTooltipTrigger variant="secondary" size="sm" (click)="handleSoftDel()">
              <lucide-angular [img]="Trash2" size="16" strokeWidth="2"/>
            </button>
            <ng-template [sideOffset]="12" rdxTooltipContent>
              <ori-tooltip-content class="dark px-2 py-1 text-xs">
                Delete Row(s)
              </ori-tooltip-content>
            </ng-template>
          </ng-container>
        </div>
      </ng-template>
    `,
    animations: [
      trigger('slideIn', [
        // Define states
        state('in', style({
          opacity: 1,
          transform: 'translateY(0) scale(1)'
        })),
        state('out', style({
          opacity: 0,
          transform: 'translateY(20px) scale(0.95)'
        })),
        
        // Entry animation
        transition('void => in', [
          style({
            opacity: 0,
            transform: 'translateY(20px) scale(0.95)'
          }),
          animate('0.2s ease-out', style({
            opacity: 1,
            transform: 'translateY(0) scale(1)'
          }))
        ]),
        
        // Exit animation
        transition('in => void', [
          style({
            opacity: 1,
            transform: 'translateY(0) scale(1)'
          }),
          animate('0.15s ease-in', style({
            opacity: 0,
            transform: 'translateY(20px) scale(0.95)'
          }))
        ])
      ])
    ]
})
export class CheckboxHeader<T> {
  private static counter = 0;
  readonly Download = Download;
  readonly FileSpreadsheet = FileSpreadsheet;
  readonly FileText = FileText;
  readonly X = X;
  readonly Trash2 = Trash2;
  context = injectFlexRenderContext<HeaderContext<T, unknown>>();
  selectedRowData: T[] = [];
  
  checkboxId = `header-checkbox-${++CheckboxHeader.counter}`;

  // Animation state
  get animationState() {
    return this.shouldShowOverlay() ? 'in' : 'out';
  }
  
  cancel() {
    this.context.table.toggleAllRowsSelected(false);
  }

  onHeaderClick() {
    const currentlyAllSelected = this.context.table.getIsAllRowsSelected();
    const hasIndeterminate = this.isIndeterminate();
    
    const shouldSelectAll = !currentlyAllSelected || hasIndeterminate;
    this.context.table.toggleAllRowsSelected(shouldSelectAll);
    
  }

  shouldShowOverlay(): boolean {
    const someSelected = this.context.table.getIsSomeRowsSelected();
    const allSelected = this.context.table.getIsAllRowsSelected();
  
    const shouldShow = someSelected || (allSelected);
    return shouldShow;
  }

  getSelectedCount(): number {
    return this.context.table.getSelectedRowModel().rows.length;
  }

  isIndeterminate(): boolean {
    const allSelected = this.context.table.getIsAllRowsSelected();
    const someSelected = this.context.table.getIsSomeRowsSelected();
    return someSelected && !allSelected;
  }

  isChecked(): boolean {
    const allSelected = this.context.table.getIsAllRowsSelected();
    const indeterminate = this.isIndeterminate();
    return allSelected && !indeterminate;
  }

  handleSoftDel() {
    this.context.table.options.meta?.delSelectedRow();
    this.cancel();
  }

  handleSelected() {
    this.selectedRowData = this.context.table.getSelectedRowModel().rows.map(row => row.original as T);
  }

  getSelectedDataAsJson(): string {
    return JSON.stringify(this.selectedRowData);
  }


  exportToExcel() {
    this.selectedRowData = [];
    this.handleSelected();
    const data = JSON.parse(this.getSelectedDataAsJson());
    const columns =this.getColumns(data);
    const worksheet = XLSX.utils.json_to_sheet(data, {header : columns});
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'data.xlsx');
    this.cancel();
  }

    getColumns(data: any[]): string[] {
    const columns: string[] = [];
    data.forEach(row => {
      Object.keys(row).forEach(col => {
        if (!columns.includes(col)) {
          columns.push(col);
        }
      });
    });
    return columns;
  }
}